import { useRef, useState } from 'react';
import { apiClient, ApiError, type MeResponse } from '@/lib/api/client';
import { useDialogFocus } from '@/lib/useDialogFocus';
import { cn } from '@/lib/utils';
import { ImageCropModal } from '@/components/ImageCropModal';

interface Props {
  me: MeResponse;
  onClose: () => void;
  onSaved: () => void;
}

/** Server cap is 4MB. We pre-flight this on the original file picked,
 *  before crop. The cropped/compressed output is well under 4MB so the
 *  upload itself won't trip the cap. */
const MAX_BYTES = 4 * 1024 * 1024;
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.apetech.studio';

/**
 * Edit-your-own-profile modal. Mounted from the nav account group.
 * Today: display name + avatar image. Email is read-only (change-email
 * is a bigger flow — TODO when needed).
 *
 * Avatar upload uses the same R2-keyed pipeline as project/workspace
 * images. The avatar appears in chat/notes/activity in a future PR;
 * for now it's stored + reachable via /v1/files/<key>.
 */
export function ProfileModal({ me, onClose, onSaved }: Props) {
  const formRef = useDialogFocus<HTMLDivElement>();
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(me.user.name ?? '');
  const [imageKey, setImageKey] = useState<string | null>(me.user.image_key);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /** File picked, awaiting crop confirmation. */
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const nameDirty = (name.trim() || null) !== (me.user.name ?? null);

  async function saveName() {
    setBusy('name');
    setErr(null);
    try {
      await apiClient.updateMe({ name: name.trim() || null });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed');
    } finally {
      setBusy(null);
    }
  }

  function pickImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    if (!ALLOWED.includes(file.type)) {
      setErr(`Use JPG / PNG / WebP / GIF. Got ${file.type || 'unknown'}.`);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (file.size > MAX_BYTES) {
      setErr(`Max 4MB. Got ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    // Stage into the crop modal; upload happens on confirm.
    setPendingFile(file);
  }

  async function uploadCropped(blob: Blob) {
    setPendingFile(null);
    setBusy('upload');
    setErr(null);
    try {
      // Wrap Blob → File so existing FormData("image", file) helper works.
      const cropped = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      const r = await apiClient.uploadMyImage(cropped);
      setImageKey(r.image_key);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Upload failed');
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function removeImage() {
    setBusy('remove');
    setErr(null);
    try {
      await apiClient.deleteMyImage();
      setImageKey(null);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Remove failed');
    } finally {
      setBusy(null);
    }
  }

  const initial = (me.user.name || me.user.email)[0]?.toUpperCase() ?? '?';
  // Append a tiny cache-buster so a fresh upload immediately invalidates
  // any stale `/v1/files/<key>` cache (R2 + browser).
  const avatarUrl = imageKey
    ? `${API_BASE}/v1/files/${imageKey}?v=${encodeURIComponent(imageKey)}`
    : me.user.avatar_url;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        ref={formRef}
        tabIndex={-1}
        className="section-glow w-full max-w-md space-y-5 rounded-2xl bg-card p-7 shadow-2xl"
      >
        <div className="space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Profile
          </div>
          <h2 id="profile-title" className="text-2xl font-semibold tracking-tight">
            Your profile
          </h2>
        </div>

        {/* Avatar block */}
        <div className="flex items-start gap-4 rounded-lg border border-border bg-background/40 p-4">
          <div className="shrink-0">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                width={64}
                height={64}
                // Cache-busting key on the URL forces the browser to re-fetch
                // when the image_key changes — without it, swapping to a
                // newly-uploaded avatar shows the cached old image.
                key={imageKey ?? avatarUrl}
                className="block h-16 w-16 rounded-full object-cover"
              />
            ) : (
              <div className="grid h-16 w-16 place-items-center rounded-full bg-primary/15 text-xl font-semibold text-primary">
                {initial}
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept={ALLOWED.join(',')}
                onChange={pickImage}
                disabled={busy !== null}
                className="no-glow sr-only"
                id="profile-image-input"
              />
              <label
                htmlFor="profile-image-input"
                aria-busy={busy === 'upload'}
                className="cursor-pointer rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-ring/60 hover:bg-card/80"
              >
                {busy === 'upload'
                  ? 'Uploading…'
                  : avatarUrl
                    ? 'Replace photo'
                    : 'Upload photo'}
              </label>
              {imageKey && (
                <button
                  type="button"
                  onClick={removeImage}
                  disabled={busy !== null}
                  className="rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-destructive/60 hover:text-destructive disabled:opacity-60"
                >
                  {busy === 'remove' ? 'Removing…' : 'Remove'}
                </button>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              JPG / PNG / WebP / GIF · max 4MB · cropped to 500×500 · shown in chat, notes, activity.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="profile-name"
            className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
          >
            Display name
          </label>
          <input
            id="profile-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            maxLength={80}
            disabled={busy !== null}
            className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <p className="text-[10px] text-muted-foreground">
            Falls back to email if blank. Shown in chat headers + activity feed.
          </p>
        </div>

        <div className="space-y-1">
          <label
            className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
          >
            Email
          </label>
          <div className="rounded-md border border-border bg-background/40 px-3 py-2 font-mono text-sm text-muted-foreground">
            {me.user.email}
          </div>
          <p className="text-[10px] text-muted-foreground">
            Email is the sign-in identity. Contact support to change it.
          </p>
        </div>

        {err && (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border bg-background px-4 py-2.5 text-sm text-muted-foreground hover:border-ring/60 hover:text-foreground"
          >
            Close
          </button>
          <button
            type="button"
            onClick={saveName}
            disabled={!nameDirty || busy !== null}
            className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {busy === 'name' ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {pendingFile && (
        <ImageCropModal
          file={pendingFile}
          onConfirm={uploadCropped}
          onClose={() => {
            setPendingFile(null);
            if (fileRef.current) fileRef.current.value = '';
          }}
        />
      )}
    </div>
  );
}
